import { Link } from "@tanstack/react-router";
import { TRIAL_CONTACTS_PHRASE } from "@/lib/trial";
import type { ReactNode } from "react";

export type FaqItem = { q: string; a: ReactNode };
export type FaqCategory = { heading: string; items: FaqItem[] };

// Verbatim Q/A copy preserved from the prior FAQ content set.
// Numeric ranges normalized per dash guardrail (hyphen range -> "to").

const Q_DIFFERENT_1: FaqItem = {
  q: "What makes CapitalDart different?",
  a: "Four things working together: AI, data, platform, and pricing. Most competitors give you one of the four. CapitalDart combines a curated 70,000+ institutional database, deal-specific AI matching, a full pipeline workflow, and prepaid credit pricing. No retainer. No placement fee. No tail.",
};

const Q_DIFFERENT_2: FaqItem = {
  q: "Why is your contact data better than PitchBook or typical VC databases?",
  a: "Granular data at the contact level, not just the firm level. PitchBook tells you a firm's investment criteria. CapitalDart tells you which person at that firm to reach, what they personally invest in, and how to frame the outreach. This goes beyond PE and VC funds. Our contact data for family offices, strategics, real estate, hedge funds, sovereign wealth funds, and asset managers has depth the most expensive data providers do not match. The database is curated by investment bankers, not scraped.",
};

const Q_DIFFERENT_3: FaqItem = {
  q: "Why is your AI matching better than other AI tools?",
  a: "Most AI matching tools run a single embedding pass against a generic investor list and rank by similarity. CapitalDart runs a multi-stage pipeline. It extracts the actual structure of your deal (stage, sector, geography, check size, business model, traction), filters the 70,000+ database against the specific mandates of each contact, and scores each match on multiple dimensions before drafting outreach in your voice. The result is a sorted list of contacts who actually invest in deals like yours, not a list of names that share keywords with your deck.",
};

const Q_WHO: FaqItem = {
  q: "Who is CapitalDart for?",
  a: "CapitalDart is for private companies, funds, and real estate sponsors raising capital. CXOs of lower middle market companies and founders raising a seed, Series A/B/C+, or growth round. Fund GPs raising Fund I through Fund III. Real estate sponsors raising equity for a development or a fund. Specialty finance and structured credit issuers. Aviation lessors. Brand IP platforms. Borrowers looking for debt from specialty lenders and asset-based lenders. Any raise that needs institutional capital and a real outreach campaign, not just a list of names. CapitalDart spans the full range: companies raising $100M+, seed-stage companies raising their first institutional round, and everyone in between. We are not built for crowdfunding, retail capital, or pre-seed friends-and-family rounds. Minimum effective check sizes start at $1M.",
};

const Q_HOW_LONG: FaqItem = {
  q: "How long does it take to run a deal?",
  a: "Identifying 100 investors takes about 10 to 15 minutes, and a full 500-investor deal typically completes in about 30 minutes. A 10-investor list is roughly 3 to 5 minutes. Adding the Investor Readiness Report adds several minutes on top. The Financial Model Review takes 5 to 10 minutes, and the Raise Strategy Report and the Deal Update Report each take a few minutes. The pipeline matches your deck against our 70,000+ investor database, identifies the contacts most likely to fit your raise, drafts personalized email and LinkedIn outreach in your voice, and exports the package as an Excel file. Live status displays while the deal is processing.",
};

const Q_SELL: FaqItem = {
  q: "Can I use CapitalDart to sell my company?",
  a: "Yes. Describe the sale the same way you would describe a raise, or upload your deck or memo. The engine ranks strategic acquirers and financial buyers from the same 70,000+ firm database by sector, size, geography, and transaction behavior, finds the right corporate development or deal contact at each, and drafts the outreach. The Investor Readiness Report doubles as a buyer-readiness review of your materials. On a sale mandate, CapitalDart also surfaces sponsor-backed platform companies actively acquiring in your space, with the sponsors behind them and their add-on acquisitions since 2024. Acquisition activity, not stated preferences, drives the ranking.",
};


const Q_DATA_SOURCE: FaqItem = {
  q: "Where does the investor data come from?",
  a: "A proprietary institutional database built by investment bankers, not scraped from the web. 70,000+ institutional contacts covering venture capital (19,777 firms), private equity (13,600), strategic acquirers (8,977), angels and founders (6,095), family offices (4,179 single and multi), real estate funds, banks, asset managers, corporate VC, hedge funds, specialty lenders, endowments, foundations, insurance companies, pensions, sovereign wealth funds, and infrastructure investors. Each contact carries verified information on stage, sector, geography, check size, and recent investment activity. We do not aggregate from 30 random data sources and hope the matches stick. The database is curated, not scraped.",
};

const Q_CLAUDE: FaqItem = {
  q: "Can I use CapitalDart inside Claude or ChatGPT?",
  a: (
    <>
      Yes. CapitalDart connects to Claude and to ChatGPT, so you can search investors, pull verified contacts, and draft outreach directly in a conversation with either assistant. Setup takes about two minutes: see the step-by-step guide at{" "}
      <Link to="/connect" className="underline decoration-1 underline-offset-2 hover:opacity-80">
        /connect
      </Link>
      . Searching and drafting are free; contact delivery uses your account credits with a confirmation before any charge.
    </>
  ),
};

const Q_CREDITS: FaqItem = {
  q: "How do credits work?",
  a: "Credits buy investor contacts. There is one plan: $500 one time, no subscription, and it includes 500 credits. Each deal produces the ranked list, contact matches, outreach drafts, and a free Investor Readiness Report. The plan includes 5 reports, usable in any combination across the Financial Model Review, the Raise Strategy Report, the Market Comps and Valuation Report, and the Indicative Term Sheet. After those 5, each report is 25 credits. Deal Update Reports are free and unlimited. Top-ups are 100 credits for $100.",
};

const Q_TRY: FaqItem = {
  q: "Can I try it before paying?",
  a: `Yes. The free trial gives you ${TRIAL_CONTACTS_PHRASE} and a free Investor Readiness Report, with no credit card. You see the matched investor list, the report, and the outreach drafts before paying. If the output is not useful, you walk away. The free trial is the same product paying customers get.`,
};

const Q_OWN_EMAIL: FaqItem = {
  q: "Can I send the outreach from my own email?",
  a: "Yes. CapitalDart drafts the outreach in your voice and exports as an Excel file with all the email and LinkedIn copy ready to send. Click a send button on any contact and your default email client opens with the subject and body prefilled. Send from Gmail, Outlook, or whatever you use. We do not integrate bulk email send or open/click tracking inside the product on purpose. Outreach goes out from your inbox, which keeps deliverability high and your investor relationships under your direct control. Mass generic emails are a sure way to get ignored.",
};

const Q_TEAM: FaqItem = {
  q: "Can I work as a team?",
  a: "Yes. From your team page you can invite teammates by email. They share one credit pool with you, see the same deals and investor pipeline, and run deals from the shared balance. Adding a seat costs 10 credits, charged only when the teammate accepts. Your teammate needs a CapitalDart account to accept, and they can create one for free at sign up.",
};

const Q_TEAM_CREDITS: FaqItem = {
  q: "How do credits work across a team?",
  a: "All credits sit in one shared pool that anyone on the team draws from. If a teammate had credits before joining, those fold into the pool when they accept. Removing a member does not refund credits.",
};

const Q_VS_PLACEMENT: FaqItem = {
  q: "How is this different from a traditional placement agent or investment bank?",
  a: "Placement agents and investment banks charge 2% to 3% of capital raised plus $15K to $25K/mo retainers plus tail fees of 0.5% to 1% on re-ups for two or more years after the engagement ends. They rely on personal networks built over decades. They report quarterly. Park Hill (PJT Partners), Eaton Partners (Stifel), and Campbell Lutyens are the largest examples and typically will not take engagements under $200M. CapitalDart is $500 one time for the plan, which includes 500 credits. No placement fee. No retainer. No tail. You get real-time pipeline visibility, AI-matched investor lists pulled from a 70,000+ institutional database, and personalized outreach drafts ready to send. When the deal closes you keep every basis point. When Fund II closes with the same LPs, you keep every basis point on that too. We are not a replacement for the placement agent relationship when you need a senior banker in the room. We are a replacement for the placement agent process when you can run the outreach yourself, or you want to supplement your placement agent's efforts.",
};

const Q_VS_DATABASES: FaqItem = {
  q: "How is this different from PitchBook, Preqin, or Crunchbase?",
  a: "PitchBook, Preqin, and Crunchbase are databases. They give you names. CapitalDart matches investors to your specific deal, drafts personalized outreach for each contact, produces an Investor Readiness Report on your deck, and tracks pipeline status through close. Databases cost $15K to $50K per year for a seat. CapitalDart is $500 one time and includes 500 credits. Databases require you to figure out who actually fits, write the outreach yourself, and manage the pipeline manually. CapitalDart does the matching, drafts the outreach, and exports the package ready to send. If you only need raw investor data, a database subscription works. If you need the matching, outreach, and pipeline, CapitalDart replaces all three with one tool.",
};

const Q_VS_AI: FaqItem = {
  q: "How is this different from PipelineRoad, Evalyze, or other AI capital raising tools?",
  a: "PipelineRoad targets fund managers raising LP capital. Their $999/mo plus a managed outreach team is closer to a hybrid placement agent than self-serve software. CapitalDart is software-only, prepaid credits, no managed team. Evalyze, PitchVault, and Qubit Capital are founder-direct tools focused on SaaS and seed-stage tech. Free tiers and sub-$50/mo entry points. Investor databases of 10,000 to 20,000 names, all VC-skewed. CapitalDart's 70,000+ database covers institutional alternative capital across PE, real estate, structured credit, specialty finance, family offices, pensions, insurance, and sovereign wealth. The output also differs. AI competitors produce founder-friendly chatbot prose. CapitalDart drafts in an institutional banker voice. No em dashes. No AI-tells. Declarative. The right tool depends on what you are raising. SaaS seed round, $5M from VCs, lighter AI tools fit. $20M Series B from institutional growth equity, a $50M real estate fund, or a structured credit issuance, CapitalDart fits.",
};

const Q_EMAIL_READ: FaqItem = {
  q: "Does CapitalDart read my email?",
  a: "Only replies to outreach you sent through CapitalDart, and only when you connect Gmail or Outlook. It matches those replies to contacts on your deals, updates their status and adds a short note. It does not read other mail, attachments or full message bodies, and disconnecting stops it immediately.",
};

const Q_PRIVACY: FaqItem = {
  q: "Do you ever sell or share my deal information?",
  a: "No. Your deck, investor list, outreach drafts, and pipeline status stay private to your account. We do not sell your data, share it with investors before you reach out, or use it to train models. Your deal is your deal. We never resell it.",
};


const Q_SECURITY: FaqItem = {
  q: "How is my data protected?",
  a: (
    <>
      <p>
        Your deal documents and financials are confidential and we treat them that way. All data is encrypted in transit and at rest. Access controls are enforced at the database row level, so your data is visible only to you and teammates you invite. Administrative credentials are isolated to trusted server infrastructure and are never exposed to the browser. Payments are processed by Stripe; we never see or store your card details. Every account must verify its email address before it can create or modify anything, and we actively screen and block abusive signups.
      </p>
      <p style={{ marginTop: 12 }}>
        If you have a security questionnaire or specific diligence questions, email hello@capitaldart.com and we will respond directly.
      </p>
    </>
  ),
};



const Q_IRR_WHAT: FaqItem = {
  q: "What is the Investor Readiness Report?",
  a: "A structured review of the pitch materials you uploaded for a deal, delivered in 15 sections. It gives you an investor readiness score, the key strengths to leverage, the likely investor reaction, the top objections and questions to prepare for, a slide by slide review with recommended changes and revised headlines, an investor fit analysis, a data room checklist, and a final recommendation. You can read a full sample on the landing page.",
};

const Q_IRR_COST: FaqItem = {
  q: "What does the Investor Readiness Report cost?",
  a: "The Investor Readiness Report is free. It is included with a deal, and credits are used only for the matched investor contacts a deal delivers.",
};

const Q_IRR_WHEN: FaqItem = {
  q: "When do I get the Investor Readiness Report?",
  a: "With the deal itself. Choose it when you set up the deal and it is produced alongside the matched contact list and the outreach drafts. Adding it takes several minutes on top of the deal time.",
};

const Q_IRR_NEEDS: FaqItem = {
  q: "What does the Investor Readiness Report need from me?",
  a: "At least one uploaded document. A deck is best, and a teaser, memo, or one-pager also work. A text description on its own is not enough, because the report reviews what is in the document.",
};

const Q_FMR_WHAT: FaqItem = {
  q: "What is a Financial Model Review?",
  a: "A first-pass review of your financial model (.xlsx) that checks internal consistency, commercial credibility, and investor readiness. You get a 1 to 10 score, section by section grades, specific findings with worksheet and cell references, the assumptions investors will challenge, and the ten diligence questions to prepare for.",
};

const Q_FMR_TYPES: FaqItem = {
  q: "What types of financial models does it cover?",
  a: "Operating companies, investment funds, and real estate. The review adapts its checklist to the model type, and models in other languages are reviewed in full with the report written in English.",
};

const Q_FMR_MATERIALS: FaqItem = {
  q: "Is my financial model added to my deal materials?",
  a: "No. The workbook is stored as a file on your deal and reviewed as a workbook. It is never converted into text materials, and the report is visible only to your team.",
};

const Q_FMR_COST: FaqItem = {
  q: "What does a Financial Model Review cost?",
  a: "The plan includes 5 reports, usable in any combination across the Financial Model Review, the Raise Strategy Report, the Market Comps and Valuation Report, and the Indicative Term Sheet. After those 5, each review is 25 credits. Credits are charged when the review starts and returned automatically if it does not finish.",
};

const Q_FMR_TIME: FaqItem = {
  q: "How long does a review take?",
  a: "Usually 5 to 10 minutes. Very large workbooks can take up to 15.",
};

const Q_FMR_CHANGES: FaqItem = {
  q: "Does the review change my model?",
  a: "No. The review is read-only. It recommends changes; it never makes them.",
};

const Q_RSR_WHAT: FaqItem = {
  q: "What is the Raise Strategy Report?",
  a: "A plan for working the investor list CapitalDart delivered on your deal. It reads your matched pool and gives you the composition and what it means, a phased process plan, channel guidance, positioning by investor type, your send order with notes on your top contacts, sample responses for investors who pass, the lead investor path, ideas beyond direct outreach, and a weekly working plan.",
};

const Q_RSR_COST: FaqItem = {
  q: "How much does the Raise Strategy Report cost?",
  a: "The plan includes 5 reports, usable in any combination across the Raise Strategy Report, the Financial Model Review, the Market Comps and Valuation Report, and the Indicative Term Sheet. After those 5, each report is 25 credits. If a report fails to generate, credits are returned automatically.",
};

const Q_RSR_WHEN: FaqItem = {
  q: "When can I get a Raise Strategy Report?",
  a: "After your first delivery on a deal. The report reads your delivered contacts and matched pool, so it needs at least one completed delivery to work from.",
};

const Q_RSR_VS_IRR: FaqItem = {
  q: "How is this different from the Investor Readiness Report?",
  a: "The Investor Readiness Report evaluates whether your deal is ready for investors. The Raise Strategy Report tells you how to work the investor list you received: the order, the pace, the channels, and the plan.",
};

const Q_DUR_WHAT: FaqItem = {
  q: "What is the Deal Update Report?",
  a: "A read on where your raise stands. It looks at your live pipeline and reports status movement since your last report, engagement by investor type, who to prioritize with a suggested next action for each, where the process has stalled, a reset plan for the week, and a clear call on whether to expand your list.",
};

const Q_DUR_COST: FaqItem = {
  q: "How much does the Deal Update Report cost?",
  a: "Deal Update Reports are free and unlimited. They do not draw on your credits or on the 5 reports included with the plan.",
};

const Q_DUR_WHEN: FaqItem = {
  q: "When can I get a Deal Update Report?",
  a: "14 days after your first delivery on a deal, and any time after that. It is built to be repeated: each report reads your pipeline as it stands and compares it with your prior report.",
};

const Q_DUR_STATUSES: FaqItem = {
  q: "What if I have not updated my contact statuses?",
  a: "The report is honest about it. It will tell you that no activity has been recorded, read your pace against the plan, and give you a reset plan. Keeping statuses current in CapitalDart makes every following report sharper.",
};

const Q_REBATE: FaqItem = {
  q: "What is the Limited Time Rebate?",
  a: "The first 100 teams receive a $250 rebate. Complete a short questionnaire about your experience and agree to let us share your comments on this site. The rebate is issued after the questionnaire is submitted.",
};

const Q_MC_WHAT: FaqItem = {
  q: "What is the Market Comps and Valuation Report?",
  a: "A report placing your raise against recent market data for your stage and company type. It covers deal sizes, valuations, revenue, and multiples with median and quartile bands, up to ten similar recent deals with the investors who backed them, an implied valuation range from your revenue, and deal momentum by quarter. It counts toward the 5 reports included with the plan, and is 25 credits after those are used. It is informational and is not a valuation opinion. The benchmarks and the implied valuation apply to companies raising equity or selling. For fund, real estate, and loan deals this report does not compute a valuation, and the other reports are the better fit.",
};

const Q_TS_WHAT: FaqItem = {
  q: "What is the Indicative Term Sheet?",
  a: "A draft term sheet outlining the proposed economics, structure, and key terms for your raise. The format adapts to your deal: equity or mezzanine terms for companies, an LP terms summary for funds, sponsor offering terms for real estate, and a facility summary for loans.",
};

const Q_AGENTDART: FaqItem = {
  q: "What is AgentDart?",
  a: "AgentDart is a daily agent you can turn on per deal. It reviews the pipeline every day, flags interested investors going quiet, drafts never sent, and follow-ups overdue, checks your top contacts for news, and drafts follow-ups for your review. Set a goal and it paces the deal against it. You get one short email each morning. Nothing is sent to an investor except by you.",
};

const Q_NO_INVESTOR_EMAILS: FaqItem = {
  q: "Does CapitalDart send emails to investors?",
  a: "No. CapitalDart and AgentDart draft messages and track your pipeline. You review every message and send it yourself, from your own email account or with the Send button.",
};

const Q_CAPITALDART_CHAT: FaqItem = {
  q: "What is CapitalDart Chat?",
  a: "A chat built into the app that operates your account: it reviews contacts, drafts follow-ups, updates statuses, sets goals, and starts investor searches, with your confirmation before anything that consumes credits.",
};

const Q_NO_AI_SUBSCRIPTION: FaqItem = {
  q: "Do I need a Claude or ChatGPT subscription?",
  a: "No. CapitalDart Chat is included. If you already use Claude or ChatGPT, the connector gives you the same tools there.",
};

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    heading: "What makes it different",
    items: [Q_DIFFERENT_1, Q_DIFFERENT_2, Q_DIFFERENT_3],
  },
  {
    heading: "Who is this for",
    items: [Q_WHO, Q_SELL],
  },
  {
    heading: "How it works",
    items: [Q_HOW_LONG, Q_DATA_SOURCE, Q_OWN_EMAIL, Q_CLAUDE, Q_CREDITS, Q_REBATE, Q_TRY, Q_TEAM, Q_TEAM_CREDITS],
  },
  {
    heading: "Investor Readiness Report",
    items: [Q_IRR_WHAT, Q_IRR_COST, Q_IRR_WHEN, Q_IRR_NEEDS],
  },
  {
    heading: "Financial Model Review",
    items: [Q_FMR_WHAT, Q_FMR_TYPES, Q_FMR_MATERIALS, Q_FMR_COST, Q_FMR_TIME, Q_FMR_CHANGES],
  },
  {
    heading: "Raise Strategy Report",
    items: [Q_RSR_WHAT, Q_RSR_COST, Q_RSR_WHEN, Q_RSR_VS_IRR],
  },
  {
    heading: "Deal Update Report",
    items: [Q_DUR_WHAT, Q_DUR_COST, Q_DUR_WHEN, Q_DUR_STATUSES],
  },
  {
    heading: "Market Comps and Valuation Report",
    items: [Q_MC_WHAT],
  },
  {
    heading: "Indicative Term Sheet",
    items: [Q_TS_WHAT],
  },
  {
    heading: "How it compares",
    items: [Q_VS_PLACEMENT, Q_VS_DATABASES, Q_VS_AI],
  },
  {
    heading: "Privacy",
    items: [Q_PRIVACY, Q_EMAIL_READ, Q_SECURITY],
  },

  {
    heading: "AgentDart and Chat",
    items: [Q_AGENTDART, Q_NO_INVESTOR_EMAILS, Q_CAPITALDART_CHAT, Q_NO_AI_SUBSCRIPTION],
  },
];

// Landing-page subset: exactly seven, in display order.
export const LANDING_FAQ_ITEMS: FaqItem[] = [
  Q_TRY,
  Q_CREDITS,
  Q_REBATE,
  Q_DIFFERENT_1,
  Q_SELL,
  Q_VS_PLACEMENT,
  Q_DIFFERENT_2,
  Q_PRIVACY,
  Q_HOW_LONG,
  Q_CLAUDE,
  Q_TEAM,
  Q_IRR_WHAT,
  Q_FMR_WHAT,
  Q_RSR_WHAT,
  Q_DUR_WHAT,
  Q_MC_WHAT,
  Q_TS_WHAT,
  Q_AGENTDART,
  Q_NO_INVESTOR_EMAILS,
  Q_CAPITALDART_CHAT,
  Q_NO_AI_SUBSCRIPTION,
];
